/**
 * Shipment types — TS mirror of Flutter `lib/core/models/shipment.dart`.
 * Field names match the snake_case the cargo-server returns. The ApiClient
 * does NOT case-convert — we intentionally keep parity with the Flutter
 * client which also reads snake_case off the wire.
 */

export type ShipmentStatus =
  | 'pending'                 // waiting for driver claim
  | 'awaiting_approval'       // driver claimed, sender to approve
  | 'awaiting_sender_approval'
  | 'accepted'                // driver accepted (no claim flow)
  | 'picked_up'               // driver picked up
  | 'in_transit'              // on the way
  | 'at_center'               // arrived at hub
  | 'out_for_delivery'        // last-mile
  | 'delivered'               // ✓ done
  | 'cancelled'
  | 'return_requested'
  | 'returned'
  | 'disputed';

export type PaymentMethod = 'cash' | 'wallet';
export type PaidBy = 'sender' | 'receiver';
export type PickupOrDelivery = 'door' | 'center';
export type Currency = 'USD' | 'SYP' | 'AED' | 'TRY' | 'SAR' | 'JOD' | 'LBP';

export interface ShipmentImage {
  url: string;
  thumbnail_url?: string;
}

export interface Shipment {
  id: number;
  tracking_number: string;
  short_code?: string;
  status: ShipmentStatus;

  sender_id: string;
  sender_name?: string;
  sender_phone?: string;
  receiver_name: string;
  receiver_phone: string;
  receiver_address?: string;
  receiver_city?: string;
  receiver_country?: string;
  receiver_lat?: number | null;
  receiver_lng?: number | null;

  pickup_address?: string;
  pickup_city?: string;
  pickup_country?: string;
  pickup_lat?: number | null;
  pickup_lng?: number | null;
  pickup_landmark?: string;
  delivery_landmark?: string;
  pickup_method?: PickupOrDelivery;
  delivery_method?: PickupOrDelivery;
  pickup_center_id?: number | null;
  delivery_center_id?: number | null;
  pickup_center_name?: string;
  delivery_center_name?: string;

  origin_city?: string;
  destination_city?: string;
  origin_country?: string;
  destination_country?: string;

  package_description?: string;
  description?: string;             // alias used by some endpoints
  package_type?: string;
  category?: string;
  weight: number;
  dimensions?: { length: number; width: number; height: number };
  length_cm?: number;
  width_cm?: number;
  height_cm?: number;
  quantity?: number;
  estimated_value?: number;
  is_fragile?: boolean;
  requires_refrigeration?: boolean;
  requires_cooling?: boolean;
  notes?: string;
  images?: ShipmentImage[] | string[];

  price?: number;
  estimated_price?: number;
  final_price?: number;
  offered_price?: number;
  currency?: Currency;
  payment_method?: PaymentMethod;
  paid_by?: PaidBy;
  pricing_source?: 'engine' | 'driver' | 'manual';

  driver_id?: string | null;
  driver_name?: string;
  driver_verified?: boolean;
  vehicle_brand?: string;
  vehicle_model?: string;
  vehicle_color?: string;
  plate_number?: string;
  vehicle_year?: number;
  trip_id?: number | null;

  direct_request_status?: 'pending' | 'accepted' | 'rejected' | 'cancelled';
  claimed_at?: string;
  claimed_by?: string;
  at_center_at?: string;
  intake_photo_url?: string;
  cash_paid_confirmed_at?: string;

  bonus_share?: number;
  created_at: string;
  updated_at?: string;
}

/// Status flowing forward — used by the timeline component to render
/// "completed" vs "current" steps.
export const TERMINAL_STATUSES: ShipmentStatus[] = [
  'delivered',
  'cancelled',
  'returned',
  'disputed',
];

export function isTerminal(s: ShipmentStatus): boolean {
  return TERMINAL_STATUSES.includes(s);
}
